import { AutogenLayoutSettlement } from "./autogen/AutogenLayoutSettlement";
import {
    BlurEvent, ChangeEvent, ClickEvent, Component, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceMode,
    DataSourceModeChangeEvent, DropdownItem, DropdownSelectionEvent, EditRowDecorator, Label, Layout,
    LookupModelSearchEvent, LookupModelSelectionEvent, Panel, PrintableEvent, SaveButton, Snackbar, Table,
    TableContentsChangedEvent, TableRow, TableRowDisplayEvent, TableRowMode, Tabset, Textbox
} from "@mcleod/components";
import { CommonDialogs, CommonTooltips, ImageTable } from "@mcleod/common";
import { OrderPostHist, OrderPostHistProps } from "@mcleod/dispatch";
import { UsersResponsibilitySlideoutTrans } from "@mcleod/general";
import {
    Api, CompanySettings, Currency, CurrencySettings, CurrencyUtil, DisplayType, HorizontalAlignment, Model, ModelRow,
    StringUtil
} from "@mcleod/core";
import { AutoMatchProfile } from "@mcleod/dispatch/src/AutoMatchProfile";
import { PayAdjustmentUtil } from "./PayAdjustmentUtil";
import { PendingPayAdjustment } from "./PendingPayAdjustment";
import { TriumphPayTxDetail } from "./TriumphPayTxDetail";
import { ImageName } from "@mcleod/images";
import { OfficePendingPayAdjustment } from "./officepayroll/OfficePendingPayAdjustment";
import { SettlementHistory } from "./SettlementHistory";
import { TriumphNotifications } from "./TriumphNotifications";

export const READY_TO_PAY_ITEMS: DropdownItem[] = [
    new DropdownItem("Y", "Process"),
    new DropdownItem("N", "Hold"),
    new DropdownItem("V", "Void")
];

const HOURLY = "H";
const SALARY = "S";
const VOID = "V";

type TriumphPayInfoResponse = {
    total: Currency,
    has_bol_received_block: boolean,
    has_bol_received_date_block: boolean,
    has_bill_received_date_block: boolean,
    has_ready_to_bill_block: boolean,
    has_unposted_wire_block: boolean,
    has_critical_exception_block: boolean,
    fresh_payment_id: string | null,
    fresh_transaction_num: string | null,
}

const TRIUMPHPAY_UNDO_UNSAVED_MESSAGE = " This action will undo any unsaved changes to the settlement.";

export class Settlement extends AutogenLayoutSettlement {
    private payrollControl: ModelRow;
    private triumphPayFieldsUpdated: boolean;
    private triumphPayNeedsCancellation: boolean;
    private triumphPayInfo: Promise<TriumphPayInfoResponse> = null;
    private triumphPayActive: Promise<boolean>;
    //afterSearchPlusChildrenListener takes tpay values from model initially, this flag skips the first tpay check in afterExecution
    private triumphPaySkipFlag: boolean = true;

    async onLoad(): Promise<void> {
        this.payrollControl = await Model.searchSingleRecord("lme/dispatch/payroll-control");
        this.textboxOriginState.allowTooltip = false;
        this.textboxDestinationState.allowTooltip = false;
        this.tableDeductions.removeColumn("Check Date");
        this.tableDeductions.removeColumn("Check Number");
        this.tableDeductions.removeColumn("Void Date");
        this.addSearchListeners();
        this.mainDataSource.addAfterSearchPlusChildrenListener(async () => {
            this.triumphPayActive?.then(isActive => {
                if (isActive) this.triumphPayInfo = Promise.resolve({
                    total: this.activeRow.getCurrency("tpay_total", new Currency(0)),
                    has_bol_received_block: this.activeRow.getBoolean("tpay_has_bol_received_block", false),
                    has_bol_received_date_block: this.activeRow.getBoolean("tpay_has_bol_received_date_block", false),
                    has_bill_received_date_block: this.activeRow.getBoolean("tpay_has_bill_received_date_block", false),
                    has_ready_to_bill_block: this.activeRow.getBoolean("tpay_has_ready_to_bill_block", false),
                    has_unposted_wire_block: this.activeRow.getBoolean("tpay_has_unposted_wire_block", false),
                    has_critical_exception_block: this.activeRow.getBoolean("tpay_has_critical_exception_block", false),
                    fresh_payment_id: this.activeRow.get("tpay_payment_id", null),
                    fresh_transaction_num: this.activeRow.get("tpay_transaction_num", null),
                })
                this.setTriumphPayComponentState(isActive);
            });
        });
        this.tableDeductions.onEditLayoutLoaded = (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const layout = rowDecorator.layout as PendingPayAdjustment;
            layout.textboxPayeeId.enabled = false;
            layout.textboxPayeeId.required = false;
            layout.textboxOrderId.enabled = false;
            layout.textboxOrderId.quickInfoOverrideMethod = async () => {
                return Layout.getLoadedLayoutWithData("lme/settlement/OrderPayablesOrderIdQuickInfo", null, layout.activeRow.get("movement_id"));
            }
            layout.textboxMovementId.enabled = false;
            if (!layout.activeRow.isNull("glid") && layout.textboxGlid.getFirstLookupModelData() == null) {
                layout.setGlidLookup(layout.textboxGlid, layout.activeRow.get("glid"));
            }
            const currencyCode = tableRow.data.getCurrency("amount")?.getCurrencyCode() ?? this.activeRow.get("payee_currency", CurrencySettings.getTransactionalCurrency());
            layout.activeRow.setValueAndLookupModelData("currency", currencyCode, { currency_type_id: currencyCode });
        };
    }

    private async runAfterSearch() {
        this.calculateUnits(this.activeRow.get("pay_method"), false);
        await this.calculatePayTotals(null, false); // make sure all datasources are done searching before calculating totals
        this.activeRow.resetOriginalData();
        const saveButton = this.dataheaderSettlement["saveButton"];
        if (saveButton) {
            saveButton.doOnDataSourceChanged(this.sourceSettlement, false);
        }
    }

private addSearchListeners() {
    const requiredChildDataSources = [this.sourceStop, this.sourceDrsPendingDeduct];
    const completedChildDataSources = new Set<DataSource>();

    const childSearchListener = (event: DataSourceExecutionEvent) => {
        if (event.getAction() === DataSourceAction.SEARCH) {
            completedChildDataSources.add(event.dataSource);
            if (completedChildDataSources.size === requiredChildDataSources.length) {
                this.runAfterSearch();
            }
        }
    };

    requiredChildDataSources.forEach(ds => ds.addAfterExecutionListener(childSearchListener));

    this.mainDataSource.addBeforeExecutionListener((event: DataSourceExecutionEvent) => {
        if (event.getAction() === DataSourceAction.SEARCH) {
            completedChildDataSources.clear();
        }
    });
}

    async sourceSettlementAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            if (!this.activeRow.isNull("movement_id")) {
                (this.layoutImageTable as ImageTable).populateForRow("O", this.activeRow.get("order_id"));
                this.textboxOrderId.quickInfoOverrideMethod = async () => {
                    return Layout.getLoadedLayoutWithData("lme/settlement/OrderPayablesOrderIdQuickInfo", null, this.activeRow.get("movement_id"));
                }
            }
            Settlement.preventUpdateDuringProcessing(this);
            this.calculateTrailerRent();
        }
        if (event.getAction() == DataSourceAction.UPDATE && this.hasBeenSubmittedToTriumphPay()) {
            // we can treat tpay cancels or updates as successful here because if they fail the row update will fail validation on backend
            if (this.triumphPayNeedsCancellation) {
                this.triumphPayInfo = this.getTriumphPayInfo();
                this.clearTriumphPayCancellationFields();
                this.checkTriumphPayComponentState();
                const orderId = this.activeRow.get("order_id");
                const payeeName = this.activeRow.get("payee_name");
                const payTotal = await this.fetchPayTotal();
                TriumphNotifications.showCancellationSuccess(orderId, payeeName, payTotal);
            }
            else if (this.triumphPayFieldsUpdated) {
                TriumphNotifications.showUpdateSuccess(this.activeRow.get("order_id"));
            }
        }
        this.setQuickAddRowDefaults(this.tableDeductions.quickAddRow);

        // Check if Triumph is active for this payee
        await this.updateTriumphPayState();
    }

    private async updateTriumphPayState(): Promise<void> {
        const [isTriumphPayActive, isPayeeTriumphPay] = await Promise.all([
            TriumphPayTxDetail.isTriumphPayActive(),
            TriumphPayTxDetail.isPayeeTriumphPay(this.activeRow.get("payee_id"))
        ]);

        const isActive = isTriumphPayActive && isPayeeTriumphPay;
        this.triumphPayActive = Promise.resolve(isActive);

        if (this.triumphPaySkipFlag) {
            this.triumphPaySkipFlag = false;
        } else if (isActive) {
            this.triumphPayInfo = this.getTriumphPayInfo();
        }

        if (!this.triumphPayNeedsCancellation) {
            this.setTriumphPayComponentState(isActive);
        }
    }

    async sourceSettlementBeforeExecution(event: DataSourceExecutionEvent) {
        this.triumphPayBeforeExecution(event);
    }

    private triumphPayBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() !== DataSourceAction.UPDATE) return;

        const processStatus = this.activeRow.get("process_status");
        if (StringUtil.isEmptyString(processStatus) ||
            !StringUtil.equalsIgnoreCase(processStatus, "T") ||
            !StringUtil.isEmptyString(this.activeRow.get("triumphpay_transaction_num"))) {
            return;
        }

        const changedData = this.activeRow.getChangedData();
        const relevantFields = ["total_pay", "ok2pay_date", "external_invoiceno", "invoice_recv_date",
            "triumphpay_invoice_status", "invoice_status_reason", "amount", "back_office_reason_code",
            "reason_code_comment", "ready_to_pay_flag"];

        const hasFieldChanges = relevantFields.some(field => changedData[field] !== undefined);

        const hasPayAdjustmentChanges = this.sourceDrsPendingDeduct.data.some(row =>
            (row.get("process_status") !== undefined &&
            !row.getCurrency("amount")?.isEqualTo(new Currency(row.originalData?.["amount"]))) ||
            row.get("ready_to_pay_flag") === "V"
        ) || false;

        const otherPayChanged = !this.activeRow.getCurrency("other_pay")?.isEqualTo(new Currency(this.activeRow.originalData?.["other_pay"]));
        const totalPayChanged = !this.activeRow.getCurrency("total_pay")?.isEqualTo(new Currency(this.activeRow.originalData?.["total_pay"]));

        if (hasFieldChanges || (otherPayChanged && hasPayAdjustmentChanges) || totalPayChanged) {
            this.activeRow.set("pay_adjustment_updated", hasPayAdjustmentChanges);
            this.triumphPayFieldsUpdated = true;
        }

        const voidStatus = this.activeRow.get("ready_to_pay_flag") === "V";
        const voidedSoloPayAdjustment = this.activeRow.getCurrency("total_pay")?.isZero() && this.sourceDrsPendingDeduct.data?.every(row => row.get("ready_to_pay_flag") === "V");
        this.triumphPayNeedsCancellation = voidStatus || voidedSoloPayAdjustment;
    }

    private async changeCurrencyCode() {
        const newCurrencyCode = this.textboxCurrency.text;

        const [settlementPay, otherPay, payTotal, proRatedRevenue, trailerRent, perDiem, preloadCharge] = [
            this.activeRow.getCurrency("total_pay"),
            this.activeRow.getCurrency("other_pay"),
            this.activeRow.getCurrency("pay_total"),
            this.activeRow.getCurrency("prorated_revenue"),
            this.activeRow.getCurrency("trailer_amt"),
            this.activeRow.getCurrency("perdiem_pay"),
            this.activeRow.getCurrency("preload_charge")
        ];

        if (settlementPay) {
            settlementPay.setCurrencyCode(newCurrencyCode);
            this.activeRow.set("total_pay", settlementPay);
        }

        if (otherPay) {
            CurrencyUtil.convertCurrencies([otherPay], newCurrencyCode).then(([convertedOtherPay]) => {
                this.activeRow.set("other_pay", convertedOtherPay);
                this.textboxPayAdjustments.displayComponentData(this.activeRow, null, 0);
            });
        }

        if (payTotal) {
            payTotal.setCurrencyCode(newCurrencyCode);
            this.activeRow.set("pay_total", payTotal);
            this.textboxTotalPay.displayComponentData(this.activeRow, null, 0);
        }

        if (proRatedRevenue) {
            proRatedRevenue.setCurrencyCode(newCurrencyCode);
            this.activeRow.set("prorated_revenue", proRatedRevenue);
        }

        if (trailerRent) {
            trailerRent.setCurrencyCode(newCurrencyCode);
            this.activeRow.set("trailer_amt", trailerRent);
        }

        if (perDiem) {
            perDiem.setCurrencyCode(newCurrencyCode);
            this.activeRow.set("perdiem_pay", perDiem);
        }

        if (preloadCharge) {
            preloadCharge.setCurrencyCode(newCurrencyCode);
            this.activeRow.set("preload_charge", preloadCharge);
        }
    }

    static preventUpdateDuringProcessing(layout: Settlement | PendingPayAdjustment | OfficePendingPayAdjustment, recordType: string = "Settlement", screenName = "Driver Settlement Processing") {
        const carrierAgentPayee = layout.activeRow.get("drs_payee.type_of") == "R" || layout.activeRow.get("drs_payee.type_of") == "A";
        const hasPayeeProcessStatus = !layout.activeRow.isNull("drs_payee.process_status") || !layout.activeRow.isNull("off_payee.process_status");
        const isReadyToProcess = layout.activeRow.get("ready_to_pay_flag") == "Y";
        const checkPrinted = layout.activeRow.get("process_status") == "C";
        if (carrierAgentPayee && checkPrinted) {
            preventUpdate();
            const reason = `${recordType} is included on previous check #${layout.activeRow.get("check_number")}.`;
            Snackbar.showSnackbar(`${reason} ${recordType} not available for update.`, { persist: true });
        } else if (!carrierAgentPayee && hasPayeeProcessStatus && isReadyToProcess) {
            preventUpdate();
            const reason = `${recordType} is being processed in ${screenName}.`;
            Snackbar.showSnackbar(`${reason} ${recordType} not available for update.`, { persist: true });
        }
        if (hasPayeeProcessStatus) {
            layout.textboxPayeeId.imagePreName = ImageName.circleCheck;
        }

        function preventUpdate() {
            layout.forEveryChildComponent((component: Component) => {
                if (component instanceof Tabset) {
                    return;
                }
                component.enabled = false;
                if (component instanceof Table) {
                    const nestedTable: Table = component as Table;
                    nestedTable.addRowDisplayListener((event: TableRowDisplayEvent) => {
                        const row: TableRow = (event as TableRowDisplayEvent).getTableRow();
                        row.forEveryChildComponent(child => {
                            child.enabledDuringUpdate = false;
                        });
                    });
                }
            });
        }
    }

    sourceSettlementAfterModeChange(event: DataSourceModeChangeEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            this.title = "Settlement - " + this.titleSearchModeSuffix;
            this.tabStopDetails.visible = false;
            this.tabImages.visible = false;
            this.textboxOrderId.lookupModelDisabled = true;
            this.textboxMovementId.lookupModelDisabled = true;
            this.checkTriumphPayComponentState(true);
        }
        if (this.mainDataSource.mode == DataSourceMode.ADD || this.mainDataSource.mode == DataSourceMode.UPDATE) {
            this.setComponentListeners();
            if (CompanySettings.getSingleton().isAsset()) {
                this.textboxSettlementPay.tooltip = "Order Pay + Trailer Rent + Per Diem - Preload Charge";
            }
            if (this.mainDataSource.mode == DataSourceMode.ADD) {
                this.textboxPayMethod.setSelectedItemFromValue("F");
                this.title = "Settlement - New";
                this.setTriumphPayComponentState(false);
            } else {
                const orderId = this.activeRow?.get("order_id");
                const payeeName = this.textboxPayeeId.getFirstLookupModelData()?.get("name");
                this.title = `Settlement - Order ${orderId} - ${payeeName}`;
                this.buttonResponsibility.visible = true;
                this.buttonOrderHistory.visible = true;
                if (this.textboxCurrency.visible && this.textboxCurrency.enabled) {
                    this.textboxCurrency.enabled = this.activeRow.isNull("orig_posted_pay") || this.activeRow.getCurrency("orig_posted_pay").isZero();
                    if (!this.textboxCurrency.enabled) {
                        this.textboxCurrency.disabledTooltip = "Currency cannot be updated when journal entries have already been created";
                    }
                }
                const payeeID = this.activeRow?.get("payee_id");
                TriumphPayTxDetail.isPayeeTriumphPay(payeeID).then(isTriumphPay => this.setTriumphPayComponentState(isTriumphPay));
                this.setFieldsBasedOnPayMethod(this.activeRow.get("pay_method"));
            }
            this.attachPayCalculationListener();

            this.textboxReasonCode.enabled = this.activeRow.get("ready_to_pay_flag") == "N";
            this.textboxReasonCodeComment.enabled = this.activeRow.get("ready_to_pay_flag") == "N";
            if (!this.activeRow.isNull("drs_payee.process_status") && this.activeRow.get("ready_to_pay_flag") !== "Y") {
                this.textboxReadyToPayFlag.removeItemsWithValue("Y");
            }
            if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
                CurrencyUtil.configureCurrencySelector(this.textboxCurrency);
            }
        }
    }

    textboxSettlementPayOnPrintable(event: PrintableEvent) {
        if (event.printableLabel) {
            event.printableLabel.style.justifyContent = "left";
            this.textboxSettlementPay.printable = false;
        }
    }

    setComponentListeners() {
        this.textboxPayeeId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                const selection = event.selection;
                this.activeRow.set("payee_type", selection.get("drs_payee.type_of"));
                this.activeRow.set("payee_currency", selection.get("currency"));
                this.setQuickAddRowDefaults(this.tableDeductions.quickAddRow);
                if (this.mainDataSource.mode === DataSourceMode.ADD) {
                    this.activeRow.set("pay_rate", selection.get("drs_payee.loaded_pay_rate"));
                    this.activeRow.set("pay_method", selection.get("drs_payee.pay_method"));
                    this.activeRow.set("trailer_rent_pct", selection.get("drs_payee.trailer_rent"));
                    this.calculateUnits(this.activeRow.get("pay_method"));
                    this.calculatePayTotals().then(async () => {
                        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                            const currencyCode = selection.get("currency", CurrencySettings.getTransactionalCurrency());
                            this.activeRow.setValueAndLookupModelData("currency", currencyCode, { currency_type_id: currencyCode });
                            await this.changeCurrencyCode();
                        }
                    });
                }
                if (!selection.isNull("drs_payee.process_status")) {
                    this.textboxPayeeId.imagePreName = ImageName.circleCheck;
                    this.textboxReadyToPayFlag.removeItemsWithValue("Y");
                    this.textboxReadyToPayFlag.setSelectedItemFromValue("N");
                    const textboxStatus = this.tableDeductions.quickAddRow.findComponentById("textboxStatus") as Textbox;
                    if (textboxStatus) {
                        textboxStatus.removeItemsWithValue("Y");
                        textboxStatus.setSelectedItemFromValue("N");
                    }
                } else if (selection.isNull("drs_payee.process_status") && this.textboxReadyToPayFlag.resolveItems().find(item => item.value == "Y") == null) {
                    this.textboxPayeeId.imagePreName = null;
                    this.textboxReadyToPayFlag.items = READY_TO_PAY_ITEMS;
                    const textboxStatus = this.tableDeductions.quickAddRow.findComponentById("textboxStatus") as Textbox;
                    if (textboxStatus) {
                        textboxStatus.items = READY_TO_PAY_ITEMS;
                    }
                }
            }
        });
        this.textboxReadyToPayFlag.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            if (!event.hasSelection()) return;
            const selection = event.selection;

            if (selection.value == "V" && this.tableDeductions.allRows.length > 0) {
                CommonDialogs.showYesNo("Would you like to change the status of associated Pay Adjustment records to Void?", "Update Pay Adjustments").then(result => {
                    if (result) {
                        this.updatePayAdjustmentStatuses();
                    }
                    this.showTriumphPayCancelWarningToast(selection);
                });
            } else if ((selection.value == "N" || selection.value == "Y") && !this.payrollControl.getBoolean("deduct_tied_order", false)) {
                CommonDialogs.showYesNo(`Would you like to change the status of associated Pay Adjustment records to ${selection.displayValue}?`, "Update Pay Adjustments").then(result => {
                    if (result) {
                        this.updatePayAdjustmentStatuses();
                    }
                    this.showTriumphPayCancelWarningToast(selection);
                });
            } else {
                this.updatePayAdjustmentStatuses();
                this.showTriumphPayCancelWarningToast(selection);
            }

            this.textboxReasonCode.enabled = this.activeRow.get("ready_to_pay_flag") == "N";
            this.textboxReasonCodeComment.enabled = this.activeRow.get("ready_to_pay_flag") == "N";
            if (this.activeRow.get("ready_to_pay_flag") != "N") {
                this.textboxReasonCode.clear();
                this.textboxReasonCodeComment.clear();
            }
        });
        this.textboxInvoiceRecvDate.addBlurListener((event: BlurEvent) => {
            const textbox = event.target as Textbox;
            if (!textbox.isDropdownVisible()) {
                this.calculateOk2PayDate();
            }
        });
        this.textboxReasonCode.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            const deselection = event.deselection;
            this.updatePayAdjustmentReasonCodes(deselection?.get("id"));
        });
        this.textboxReasonCodeComment.addChangeListener((event: ChangeEvent) => {
            this.updatePayAdjustmentReasonCodeComments(event.newValue);
        });
        this.textboxOk2payDate.addChangeListener((event: ChangeEvent) => {
            this.updateOk2PayDates(event.newValue);
        });
        this.textboxCurrency.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                this.changeCurrencyCode();
            }
        });
        this.textboxProratedRevenue.addBlurListener((event: BlurEvent) => {
            this.changeCurrencyCode();
        });
        this.textboxPerdiemPay.addBlurListener((event: BlurEvent) => {
            this.changeCurrencyCode();
        });
        this.textboxPreloadCharge.addBlurListener((event: BlurEvent) => {
            this.changeCurrencyCode();
        });
        this.textboxPayMethod.addAfterDropdownSelectionListener((event: DropdownSelectionEvent) => {
            if (this.mainDataSource.mode !== DataSourceMode.SEARCH) {
                if (event.hasSelection()) {
                    this.calculateTrailerRent();
                    this.setFieldsBasedOnPayMethod(event.selection.value);
                }
            }
        });
    }

    showTriumphPayCancelWarningToast(selection: DropdownItem) {
        if (this.triumphPayActive == null) this.triumphPayActive = TriumphPayTxDetail.isTriumphPayActive();
        this.triumphPayActive.then(isActive => {
            if (isActive && this.hasBeenSubmittedToTriumphPay() && selection.value === "V") {
                Snackbar.showWarningSnackbar("Voiding the settlement will cancel the submitted Triumph payment(s).");
            }
        });
    }

    private attachPayCalculationListener(): void {
        this.activeRow.addAfterFieldUpdateListener(event => {
            let shouldRecalculateTotals: boolean = false;
            if (event.oldValue == event.newValue) return;
            if (this.activeRow.get("pay_method") === "M" && event.fieldName == "pay_distance") {
                shouldRecalculateTotals = true;
            } else if (this.activeRow.get("pay_method") === "C" && event.fieldName == "weight") {
                shouldRecalculateTotals = true;
            } else if (this.activeRow.get("pay_method") === "T" && event.fieldName == "weight") {
                shouldRecalculateTotals = true;
            } else if (this.activeRow.get("pay_method") === "P" && event.fieldName == "prorated_revenue") {
                shouldRecalculateTotals = true;
            }
            const otherFieldsToRecalculate = ["linehaul_charge", "trailer_rent_pct", "pay_rate", "pay_method", "perdiem_pay", "preload_charge"];
            if (otherFieldsToRecalculate.includes(event.fieldName)) {
                shouldRecalculateTotals = true;
            }
            if (event.fieldName == "trailer_rent_pct" || event.fieldName == "linehaul_charge") {
                this.calculateTrailerRent();
            }

            if (shouldRecalculateTotals) {
                const shouldRecalculatePerDiem = event.fieldName == "prorated_revenue" || event.fieldName == "pay_distance" || event.fieldName == "pay_method";
                this.calculateUnits(this.activeRow.get("pay_method"), shouldRecalculatePerDiem);
                this.calculatePayTotals();
            }
        });
    }

    buttonOrderHistoryOnClick(event: ClickEvent) {
        const props: OrderPostHistProps = {
            orderType: "ORDER",
            orderId: this.activeRow.get("order_id"),
            movementId: this.activeRow.get("movement_id"),
            isBilled: true
        };
        OrderPostHist.showInSlideout(
            props, DataSourceMode.UPDATE, this.buttonOrderHistory);
    }

    buttonResponsibilityOnClick(event: ClickEvent) {
        const urs = new UsersResponsibilitySlideoutTrans("lme/dispatch/MovementUsers", "Movement Users - " + this.activeRow.get("movement_id"), { id: this.activeRow.get("movement_id") }, [], this.sourceSettlement, this.sourceResponsibleHist);
        urs.show();
    }

    async buttonCancelTriumphpayPaymentOnClick() {
        this.triumphPayInfo = this.getTriumphPayInfo();
        const enabled = await this.isCancelEnabled();
        if (!enabled) {
            TriumphNotifications.showErrorSnackbar("Failed to cancel Triumph payment (It was likely already canceled elsewhere).", false);
            this.checkTriumphPayComponentState();
            return;
        }
        this.buttonCancelTriumphpayPayment.busy = true;
        CommonDialogs.showYesNo(this.getCancelTriumphPayDialog(), "Confirm Triumph Cancellation").then(async result => {
            try {
                if (result) {
                    const response = await Api.post("lme/settlement/triumphpay-actions", {
                        settlement_id: this.activeRow.get("id"),
                        action_type: "CANCEL_PAYMENT"
                    });

                    const responseData = response.data[0];

                    if (responseData?.success) {
                        this.buttonCancelTriumphpayPayment.enabled = false;
                        await this.search(this.mainDataSource.lastSearch);
                        await this.sourceDrsPendingDeduct.search(this.sourceDrsPendingDeduct.lastSearch);
                        this.attachPayCalculationListener();
                        this.calculatePayTotals();

                        const payTotal = await this.fetchPayTotal();
                        TriumphNotifications.showCancellationSuccess(
                            this.activeRow.get("order_id"),
                            this.activeRow.get("payee_name"),
                            payTotal
                        );
                    } else {
                        TriumphNotifications.showErrorSnackbar(responseData?.error, false);
                    }
                }
            } catch (error) {
                Snackbar.showWarningSnackbar("An error occurred while canceling the settlement: " + error.message);
            } finally {
                this.buttonCancelTriumphpayPayment.busy = false;
                this.buttonCancelTriumphpayPayment.enabled = false;
                this.checkTriumphPayComponentState();
            }
        });
    }

    getCancelTriumphPayDialog(): Panel {
        const content: Panel = new Panel({ margin: 0, padding: 0, minWidth: 400, rowBreakDefault: true });
        const messageText = "Are you sure you want to cancel the submitted Triumph payment(s)?";
        const messageLabel = new Label({ text: messageText, width: 350, fillRow: true, align: HorizontalAlignment.CENTER, paddingBottom: 10 });
        const unsavedChangesMessage = new Label({ text: TRIUMPHPAY_UNDO_UNSAVED_MESSAGE, width: 350, fillRow: true, align: HorizontalAlignment.CENTER });
        content.add(messageLabel, unsavedChangesMessage);
        return content;
    }

    async buttonReadyToSubmitOnClick(event: ClickEvent) {
        this.triumphPayInfo = this.getTriumphPayInfo();
        const { enabled } = await this.isSubmissionEnabled();
        if (!enabled) {
            TriumphNotifications.showErrorSnackbar("Failed to submit settlement to Triumph", false);
            this.checkTriumphPayComponentState();
            return;
        }
        this.buttonReadyToSubmit.busy = true;
        CommonDialogs.showYesNo(await this.getTriumphPaySubmissionDialog(), "Submit to Triumph").then(async submit => {
            try {
                if (submit) {
                    const response = await Api.post("lme/settlement/submit-settlement-to-triumphpay", {
                        settlement_id_list: [this.activeRow.get("id")]
                    });

                    const { error, successful_settlement_count, is_update } = response?.data[0];

                    if (successful_settlement_count > 0) {
                        this.buttonReadyToSubmit.enabled = false;
                        await this.search(this.mainDataSource.lastSearch);
                        await this.sourceDrsPendingDeduct.search(this.sourceDrsPendingDeduct.lastSearch);
                        this.attachPayCalculationListener();
                        this.calculatePayTotals();

                        TriumphNotifications.showSubmissionSuccess(
                            this.activeRow.get("order_id"),
                            this.activeRow.get("payee_name")
                        );
                    } else {
                        TriumphNotifications.showErrorSnackbar(error, is_update ?? false);
                    }
                }
            } catch (error) {
                // Network/connection error - show generic message
                Snackbar.showWarningSnackbar("Failed to connect to Triumph. Please try again later.");
            } finally {
                this.buttonReadyToSubmit.busy = false;
                this.buttonReadyToSubmit.enabled = false;
                this.checkTriumphPayComponentState();
            }
        });
    }

    async getTriumphPaySubmissionDialog(): Promise<Panel> {
        const content: Panel = new Panel({ margin: 0, padding: 0, minWidth: 400, rowBreakDefault: true });
        const messageText = "Are you sure you want to submit the settlement to Triumph?" + TRIUMPHPAY_UNDO_UNSAVED_MESSAGE;
        const message = new Label({ text: messageText, width: 350, fillRow: true, align: HorizontalAlignment.CENTER, paddingBottom: 10 });
        const panel = new Panel({ borderWidth: 1, borderColor: "subtle.light", borderRadius: 4, fillRow: true });
        const orderId = new Textbox({ caption: "Order ID", text: this.activeRow.get("order_id"), printable: true, rowBreak: false, marginRight: 10 });
        const payee = new Textbox({ caption: "Payee", text: this.activeRow.get("payee_name"), printable: true, fillRow: true, rowBreak: false, marginRight: 10 });
        const total = new Textbox({ caption: "Total Amount", text: (await this.triumphPayInfo).total.toString(), printable: true, rowBreak: false, displayType: DisplayType.CURRENCY });
        panel.add(orderId);
        panel.add(payee);
        panel.add(total);
        content.add(message, panel);
        return content;
    }

    async getTriumphPayInfo() {
        if (this.owner instanceof SettlementHistory) return null;
        const result = await Api.post("lme/settlement/triumphpay-settlement-info", { settlement_id: this.activeRow.get("id") });
        const responseData = result.data[0];
        responseData.total = new Currency(responseData.total);
        return responseData as TriumphPayInfoResponse;
    }

    setQuickAddRowDefaults(quickAddRow: TableRow) {
        if (quickAddRow && this.activeRow) {
            quickAddRow.data.set("ready_to_pay_flag", this.activeRow.get("ready_to_pay_flag"));
            if (!this.activeRow.isNull("drs_payee.process_status")) {
                const textboxStatus = quickAddRow.findComponentById("textboxStatus") as Textbox;
                if (textboxStatus) {
                    textboxStatus.removeItemsWithValue("Y");
                    textboxStatus.setSelectedItemFromValue(this.activeRow.get("ready_to_pay_flag"));
                }
            }
            quickAddRow.data.set("transaction_date", this.activeRow.get("ok2pay_date"));
            quickAddRow.data.setValueAndLookupModelData("back_office_reason_code", this.activeRow.get("back_office_reason_code"), this.activeRow.getLookupModelData("back_office_reason_code"));
            quickAddRow.data.set("reason_code_comment", this.activeRow.get("reason_code_comment"));
            quickAddRow.data.set("order_id", this.activeRow.get("order_id"));
            quickAddRow.data.setLookupModelData("order_id", this.activeRow.getLookupModelData("order_id"));
            quickAddRow.data.set("movement_id", this.activeRow.get("movement_id"));
            quickAddRow.data.set("payee_id", this.activeRow.get("payee_id"));
            quickAddRow.data.setLookupModelData("payee_id", this.activeRow.getLookupModelData("payee_id"));
            const amount = quickAddRow.data.getCurrency("amount", 0);
            if (amount && CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                amount.setCurrencyCode(this.activeRow.get("payee_currency", this.textboxCurrency.text));
                quickAddRow.data.set("amount", amount);
            }
            quickAddRow.displayComponentValues();
        }
    }

    tableDeductionsOnRowDisplay(event: TableRowDisplayEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            return;
        }
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const textboxAmount = tableRow.findComponentById("textboxAmount") as Textbox;
        if (textboxAmount) {
            textboxAmount.enabled = false;
        }

        const postModule = modelRow.getString("post_module", "");
        if (PendingPayAdjustment.fromAPFUELWIRE(postModule)) {
            const adjustmentAccountRequiredMessage = "This record originated from AP, Fuel, or Wires. To update this field, please open this record from the Pending Pay Adjustments screen.";
            const textboxUnits = tableRow.findComponentById("textboxUnits") as Textbox;
            if (textboxUnits) {
                textboxUnits.enabled = false;
                textboxUnits.disabledTooltip = adjustmentAccountRequiredMessage;
            }
            const textboxRate = tableRow.findComponentById("textboxRate") as Textbox;
            if (textboxRate) {
                textboxRate.enabled = false;
                textboxRate.disabledTooltip = adjustmentAccountRequiredMessage;
            }
            const textboxDeductCodeId = tableRow.findComponentById("textboxDeductCodeId") as Textbox;
            if (textboxDeductCodeId) {
                textboxDeductCodeId.enabled = false;
                textboxDeductCodeId.disabledTooltip = adjustmentAccountRequiredMessage;
            }
        }

        this.checkHoldReasonFields(tableRow);

        if (modelRow["hasFieldListener"] != true) {
            modelRow.addAfterFieldUpdateListener(event => {
                if (PendingPayAdjustment.requiresAdjustmentAccount(modelRow)) {
                    if (event.fieldName == "ready_to_pay_flag") {
                        if (event.newValue == "V") {
                            modelRow.set("ready_to_pay_flag", event.oldValue);
                            const adjustmentAccountRequiredMessage = "An adjustment account is required to void a record from AP, Fuel, or Wires. Please update this record from the Pending Pay Adjustments screen.";
                            Snackbar.showWarningSnackbar(adjustmentAccountRequiredMessage, { targetPanel: this });
                        }
                    }
                }
                if (event.fieldName == "units" || event.fieldName == "rate") {
                    const units = modelRow.get("units", 1);
                    const rate = modelRow.get("rate", 0);
                    const amount = modelRow.getCurrency("amount", 0);
                    amount.setAmount(units * rate);
                    modelRow.set("amount", amount);
                    if (tableRow.mode == TableRowMode.QUICK_ADD) {
                        const textboxAmount = tableRow.findComponentById("textboxAmount") as Textbox;
                        if (textboxAmount) {
                            textboxAmount.displayComponentData(modelRow, null, tableRow.index);
                        }
                    }
                }
                if (event.fieldName == "ready_to_pay_flag") {
                    this.checkHoldReasonFields(tableRow);
                }
            });
        }
        modelRow["hasFieldListener"] = true;

        if (tableRow.mode == TableRowMode.QUICK_ADD) {
            this.setQuickAddRowDefaults(tableRow);
        }
    }

    private checkHoldReasonFields(tableRow: TableRow): void {
        const modelRow = tableRow.data as ModelRow;
        const textboxBackOfficeReasonCode = tableRow.findComponentById("textboxBackOfficeReasonCode") as Textbox;
        if (textboxBackOfficeReasonCode) {
            textboxBackOfficeReasonCode.enabled = modelRow.get("ready_to_pay_flag") == "N";
        }
        const textboxPayReasonCodeComment = tableRow.findComponentById("textboxPayReasonCodeComment") as Textbox;
        if (textboxPayReasonCodeComment) {
            textboxPayReasonCodeComment.enabled = modelRow.get("ready_to_pay_flag") == "N";
        }
        if (modelRow.get("ready_to_pay_flag") != "N") {
            modelRow.set("back_office_reason_code", null);
            modelRow.set("reason_code_comment", null);
        }
    }

    textboxDeductCodeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
            return;
        }
        if (event.hasSelection()) {
            const selection = event.selection;
            const tableRow = TableRow.getContainingTableRow(event.target as Textbox);
            const modelRow = tableRow.data as ModelRow;
            if (!PayAdjustmentUtil.validDeductCodeChange(modelRow.originalData?.["deduction_type"], selection.get("code_type"), modelRow.getCurrency("gl_accrual_amt"), this)) {
                modelRow.revertToOriginalData("deduct_code_id");
                return;
            }
            modelRow.set("short_desc", selection.get("short_desc"));
            modelRow.set("glid", selection.get("glid"));
            modelRow.set("deduction_type", selection.get("code_type"));
            modelRow.set("rate", selection.getCurrency("amount")?.amount);
            if (PendingPayAdjustment.requiresAdjustmentAccount(modelRow)) {
                modelRow.set("rate", modelRow.originalData?.["rate"]);
            }
            if (tableRow.mode == TableRowMode.QUICK_ADD) {
                tableRow.displayComponentValues();
            }
        }
    }

    calculateUnits(payMethod: string, recalculatePerDiemPay: boolean = true) {
        if (payMethod == null) {
            this.textboxPayUnits.text = "0";
            return;
        }
        switch (payMethod) {
            case "F": { // Flat
                this.textboxPayUnits.text = "1";
                break;
            }
            case "P": { // Percentage
                this.textboxPayUnits.text = this.activeRow.getCurrency("prorated_revenue", 0).amount?.toString() ?? "0";
                if (recalculatePerDiemPay) {
                    const perDiemAmount = new Currency(this.activeRow.get("drs_payee.perdiem_rate", 0) * this.activeRow.getCurrency("prorated_revenue", 0).amount / 100);
                    this.textboxPerdiemPay.text = perDiemAmount.toString();
                    this.activeRow.set("perdiem_pay", perDiemAmount);
                }
                break;
            }
            case "M": { // Pay Distance
                this.textboxPayUnits.text = (this.activeRow.getNumber("pay_distance", 0))?.toString();
                if (recalculatePerDiemPay) {
                    const perDiemAmount = new Currency(this.activeRow.get("drs_payee.perdiem_rate", 0) * this.activeRow.getNumber("pay_distance", 0) / 100);
                    this.textboxPerdiemPay.text = perDiemAmount.toString();
                    this.activeRow.set("perdiem_pay", perDiemAmount);
                }
                break;
            }
            case "C": { // CWT (Hundred Weight)
                this.textboxPayUnits.text = (this.activeRow.getNumber("weight", 0) / 100)?.toString();
                break;
            }
            case "T": { // Tons
                this.textboxPayUnits.text = (this.activeRow.getNumber("weight", 0) / 2000)?.toString();
                break;
            }
        }
        const displayType = payMethod == "P" ? DisplayType.PERCENTAGE : DisplayType.DECIMAL;
        if (displayType != this.textboxPayRate.displayType) {
            this.textboxPayRate.displayType = displayType;
            this.textboxPayRate.displayComponentData(this.activeRow, null, 0);
        }
    }

    async calculatePayTotals(payRate?: string, reCalcSettlementPay: boolean = true, payAdjustmentRows: ModelRow[] = this.sourceDrsPendingDeduct.data) {
        let orderPay = this.activeRow.getCurrency("order_pay");
        const currencyCode = this.activeRow.get("currency", this.activeRow.originalData?.["currency"]);
        if (reCalcSettlementPay) {
            let rate = Number.parseFloat(payRate) || this.textboxPayRate.getDataValue() || 0;
            if (this.activeRow.get("pay_method") == "P" || this.activeRow.get("pay_method") == "M") {
                rate = rate / 100;
            }
            const units = Number.parseFloat(this.textboxPayUnits.text) || 0;
            orderPay = new Currency(units * rate);
            if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                orderPay.setCurrencyCode(currencyCode);
                orderPay.setConversionDate(this.activeRow.getCurrency("linehaul_charge")?.getConversionDate());
            }
            this.activeRow.set("order_pay", orderPay);
        }
        // always derive from order_pay/perdiem/trailer/preload so total_pay reflects current data even when it wasn't persisted correctly (e.g. manually-entered records)
        const totalPay = orderPay.getClone(orderPay.getAmount());
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            totalPay.setCurrencyCode(currencyCode);
            totalPay.setConversionDate(this.activeRow.getCurrency("linehaul_charge")?.getConversionDate());
        }
        await totalPay.add(this.activeRow.getCurrency("perdiem_pay"));
        await totalPay.add(this.activeRow.getCurrency("trailer_amt"));
        await totalPay.subtract(this.activeRow.getCurrency("preload_charge"));
        this.activeRow.set("total_pay", totalPay);
        const payAdjustments = new Currency(0);
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            payAdjustments.setCurrencyCode(currencyCode);
            payAdjustments.setConversionDate(this.activeRow.getCurrency("linehaul_charge")?.getConversionDate());
        }
        const payAdjustmentAmounts: Currency[] = payAdjustmentRows.map(row => row.getCurrency("amount"));
        const payAdjustmentConvertedAmounts = await CurrencyUtil.convertCurrencies(payAdjustmentAmounts, currencyCode);
        for (let i = 0; i < payAdjustmentRows.length; i++) {
            const row = payAdjustmentRows[i];
            if (row.get("ready_to_pay_flag") == "V" || row.get("deduction_type") == "P") {
                continue;
            }
            const convertedAmount = payAdjustmentConvertedAmounts[i]; // using the already converted amounts to avoid making an API call for exchange rates per add
            if (row.get("deduction_type") == "E" || row.get("deduction_type") == "R") {
                await payAdjustments.add(convertedAmount);
            } else {
                await payAdjustments.subtract(convertedAmount);
            }
            if (row._appending) {
                const amount = row.getCurrency("amount");
                amount.setConversionDate(this.activeRow.getCurrency("total_pay")?.getConversionDate());
                row.set("amount", amount);
            }
        }
        this.activeRow.set("other_pay", payAdjustments);
        this.textboxPayAdjustments.displayComponentData(this.activeRow, null, 0);
        const payTotal = totalPay.getClone(totalPay.getAmount());
        await payTotal.add(payAdjustments);
        this.activeRow.set("pay_total", payTotal);
        this.activeRow.set("total_pay", totalPay);
        this.textboxTotalPay.displayComponentData(this.activeRow, null, 0);
        this.calculateTrailerRent();
    }

    tableDeductionsOnContentsChanged(event: TableContentsChangedEvent) {
        this.calculatePayTotals(null, false);
    }

    textboxPayAdjustmentsOnPrintable(event: PrintableEvent) {
        event.printableLabel.align = HorizontalAlignment.LEFT; // temp code until architecture supports overriding alignment for currency fields
    }

    textboxTotalPayOnPrintable(event: PrintableEvent) {
        event.printableLabel.align = HorizontalAlignment.LEFT; // temp code until architecture supports overriding alignment for currency fields
    }

    private updatePayAdjustmentStatuses() {
        this.tableDeductions.allRows.forEach(row => {
            if (row.data.get("ready_to_pay_flag") != "V") {
                row.data.set("ready_to_pay_flag", this.activeRow.get("ready_to_pay_flag"));
            }
        });
    }

    private updatePayAdjustmentReasonCodes(oldValue: string) {
        this.tableDeductions.allRows.forEach(row => {
            if (row.data.get("back_office_reason_code") == oldValue || row.data.isNull("back_office_reason_code")) {
                row.data.setValueAndLookupModelData("back_office_reason_code", this.activeRow.get("back_office_reason_code"), this.activeRow.getLookupModelData("back_office_reason_code"));
                row.data.set("reason_code_comment", this.activeRow.get("reason_code_comment"));
            }
        });
    }

    private updatePayAdjustmentReasonCodeComments(newValue: string) {
        this.tableDeductions.allRows.forEach(row => {
            if (row.data.get("back_office_reason_code") == this.activeRow.get("back_office_reason_code")) {
                row.data.set("reason_code_comment", newValue);
            }
        });
    }

    private updateOk2PayDates(newValue: Date) {
        const newDate = new Date(newValue);
        this.tableDeductions.allRows.forEach(row => {
            if (row.data.getDate("transaction_date") <= newDate) {
                row.data.set("transaction_date", newDate);
            }
        });
    }

    // populates settlement record with data from order/movement
    textboxOrderIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        this.handleMovementLookupSelection(event);
    }

    textboxMovementIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode === DataSourceMode.SEARCH) return;
        this.handleMovementLookupSelection(event);
    }

    private handleMovementLookupSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selection = event.selection;

            // Movement fields
            this.activeRow.setValueAndLookupModelData("movement_id", selection.get("id"), { id: selection.get("id") });
            this.activeRow.set("pay_distance", selection.get("move_distance"));
            this.activeRow.set("external_invoiceno", selection.get("trip_lease"));
            this.activeRow.set("invoice_recv_date", selection.get("invoice_recv_date"));
            this.activeRow.set("loaded", selection.get("loaded"));
            this.calculateOk2PayDate();
            this.activeRow.set("manifest_id", selection.get("manifest_id"));

            // Order fields
            this.activeRow.set("commodity_id", selection.get("orders.commodity_id"));
            if (!this.activeRow.isNull("commodity_id")) {
                AutoMatchProfile.applyLookupModelData(this.textboxCommodityId, this.activeRow).then(() => {
                    this.textboxCommodityId.displayComponentData(this.activeRow, null, 0);
                });
            }
            this.activeRow.set("revenue_id", selection.get("orders.revenue_code_id"));
            if (!this.activeRow.isNull("revenue_id")) {
                AutoMatchProfile.applyLookupModelData(this.textboxRevenueId, this.activeRow).then(() => {
                    this.textboxRevenueId.displayComponentData(this.activeRow, null, 0);
                });
            }
            this.activeRow.set("weight", selection.get("orders.weight"));
            this.activeRow.set("billed_distance", selection.get("orders.bill_distance"));
            this.activeRow.set("bill_of_lading_no", selection.get("orders.blnum"));
            this.activeRow.set("linehaul_charge", selection.get("orders.freight_charge"));
            const totalPay = this.activeRow.getCurrency("total_pay");
            totalPay.setConversionDate(this.activeRow.getCurrency("linehaul_charge")?.getConversionDate());
            this.activeRow.set("total_pay", totalPay);

            // Prorated Move fields
            this.activeRow.set("prorated_revenue", selection.get("prorated_moveorder.prorated_revenue"));
            this.activeRow.set("order_tot_dist", selection.get("prorated_moveorder.total_distance"));

            // Stop fields
            this.activeRow.set("origin_city", selection.get("stop_origin.city_name"));
            this.activeRow.setValueAndLookupModelData("origin_state", selection.get("stop_origin.state"), { "id": selection.get("stop_origin.state") });
            this.activeRow.set("destination_city", selection.get("stop_dest.city_name"));
            this.activeRow.setValueAndLookupModelData("destination_state", selection.get("stop_dest.state"), { "id": selection.get("stop_dest.state") });
            this.activeRow.set("ship_date", selection.get("stop_origin.sched_arrive_early"));
            this.activeRow.set("delivery_date", selection.get("stop_dest.sched_arrive_early"));
            this.sourceStop.search({ movement_id: this.activeRow.get("movement_order.movement_id") });

            CommonTooltips.setTooltipContentLayout(this.textboxOrderId, this.activeRow.get("movement_id"), "lme/settlement/OrderPayablesOrderIdQuickInfo");
        }
    }

    calculateOk2PayDate() {
        if (!(this.activeRow.get("payee_type") == "R") || this.activeRow.isNull("invoice_recv_date") || this.activeRow.isNull("payee_id") || this.activeRow.isNull("movement_id")) {
            return;
        }
        Api.search("lme/settlement/update-ok2pay-date", {
            movement_id: this.activeRow.get("movement_id"),
            invoice_recv_date: this.activeRow.getDate("invoice_recv_date"),
            payee_id: this.activeRow.get("payee_id")
        }).then(response => {
            if (response?.data[0]?.ok2pay_date) {
                this.activeRow.set("ok2pay_date", new Date(response.data[0].ok2pay_date));
            }
        });
    }

    calculateTrailerRent() {
        if (this.activeRow.get("payee_type") == "O" && ![HOURLY, SALARY].includes(this.activeRow.get("pay_method"))) {
            this.activeRow.set("trailer_amt", new Currency(this.activeRow.getCurrency("linehaul_charge", 0).amount * this.activeRow.get("trailer_rent_pct", 0) / 100));
        }
    }

    textboxDeductCodeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    textboxReasonCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
        event.filter.code_type = "S";
    }

    textboxBackOfficeReasonCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
        event.filter.code_type = "S";
    }

    textboxOrderIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.textboxOrderId.text?.length < this.textboxOrderId.lookupModelMinChars) {
            event.preventDefault();
        } else {
            event.filter.order_id = this.textboxOrderId.text;
            event.filter.lm_search = this.textboxOrderId.text;
        }
    }

    textboxMovementIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.textboxOrderId.text?.length < this.textboxOrderId.lookupModelMinChars) {
            event.preventDefault();
        } else {
            event.filter.order_id = this.textboxOrderId.text;
            event.filter.lm_search = this.textboxOrderId.text;
        }
    }

    private async fetchPayTotal() {
        let payTotal: string;
        if (this.activeRow.get("ready_to_pay_flag") == VOID) {
            // Use the pay_total field when status is VOID
            payTotal = this.activeRow.getCurrency("pay_total", new Currency(0)).toString();
        } else {
            // Use the total_pay field when not VOID. Note: pay_total and total_pay are distinct fields
            const info = this.triumphPayInfo ? await this.triumphPayInfo : null;
            payTotal = info?.total?.toString() ?? this.activeRow.getCurrency("total_pay", new Currency(0)).toString();
        }
        return payTotal;
    }

    private clearTriumphPayCancellationFields() {
        this.activeRow.set("triumphpay_pymt_id", null);
        this.activeRow.set("triumphpay_pymt_status", null);
        this.activeRow.set("triumphpay_transaction_date", null);
        this.activeRow.set("triumphpay_transaction_num", null);
        this.activeRow.set("triumphpay_carrierpay_date", null);
        this.activeRow.set("triumphpay_confirm_code", null);
        this.activeRow.set("triumphpay_pymt_details", null);
        this.activeRow.set("process_status", null);
        this.activeRow.set("triumphpay_quickpay", false);
        this.activeRow.set("triumphpay_process_date", new Date());
    }

    private checkTriumphPayComponentState(isSearch: boolean = false) {
        if (this.triumphPayActive == null) this.triumphPayActive = TriumphPayTxDetail.isTriumphPayActive();
        this.triumphPayActive.then(isActive => this.setTriumphPayComponentState(isActive, isSearch));
    }

    private setTriumphPayComponentState(value: boolean, isSearch: boolean = false) {
        this.tabTriumphPay.visible = value;
        const isSettlementHistory = this.owner instanceof SettlementHistory;
        const notSearchOrHistory = !isSearch && !isSettlementHistory;
        this.buttonReadyToSubmit.visible = value && notSearchOrHistory;
        this.buttonCancelTriumphpayPayment.visible = value && !isSettlementHistory;

        // Ensure triumphPayInfo is loaded before checking button states
        if (this.activeRow && value && this.triumphPayInfo == null) {
            this.triumphPayInfo = this.getTriumphPayInfo();
        }

        if (this.activeRow && this.triumphPayInfo != null) {
            // Submit button requires update mode
            if (notSearchOrHistory) {
                this.setTriumphPaySubmissionEnabled(value);
            }
            // Cancel button can be enabled in both search and update mode
            if (!isSettlementHistory) {
                this.setTriumphPayCancelEnabled(value);
            }
            this.buttonTriumphPayLink.visible = this.activeRow.get("process_status") === "T" && value;
        }
        else {
            this.buttonTriumphPayLink.visible = false;
        }
    }

    private async setTriumphPayCancelEnabled(value: boolean) {
        this.buttonCancelTriumphpayPayment.enabled = value && await this.isCancelEnabled();
    }

    private async isCancelEnabled(): Promise<boolean> {
        return this.triumphPayInfo.then(info => {
            return !!info.fresh_payment_id && !info.fresh_transaction_num;
        });
    }

    private async setTriumphPaySubmissionEnabled(value: boolean) {
        if (!value) {
            this.buttonReadyToSubmit.enabled = false;
            this.buttonReadyToSubmit.disabledTooltip = null;
            return;
        }
        const { enabled, tooltip } = await this.isSubmissionEnabled();
        this.buttonReadyToSubmit.enabled = enabled;
        this.buttonReadyToSubmit.disabledTooltip = tooltip;
    }

    private async isSubmissionEnabled(): Promise<{ enabled: boolean, tooltip: string }> {
        const disabledMap = await this.getTriumphPaySubmissionDisabledMap();
        let enabled = true;
        let tooltip = "";
        for (const [t, isEnabled] of disabledMap.entries()) {
            if (!isEnabled()) {
                enabled = false;
                tooltip += `• ${t}\n`;
            }
        }
        return { enabled, tooltip: tooltip.trim() };
    }

    private async getTriumphPaySubmissionDisabledMap(): Promise<Map<string, () => boolean>> {
        const disabledMap: Map<string, () => boolean> = new Map<string, () => boolean>();

        const triumphPayInfo = await this.triumphPayInfo;
        disabledMap.set("Payee must have payment method of Triumph", () => !(this.activeRow.get("payment_method") == "U"));
        disabledMap.set("Settlement has an existing process status", () => StringUtil.isEmptyString(this.activeRow.get("process_status")));
        disabledMap.set("Settlement submitted to Triumph", () => !this.hasBeenSubmittedToTriumphPay());
        disabledMap.set("Triumph invoice status is on hold", () => !(this.activeRow.get("triumphpay_invoice_status") == "3"));
        disabledMap.set("Settlement has void status", () => !(this.activeRow.get("ready_to_pay_flag") == "V"));
        disabledMap.set("Settlement is on hold", () => StringUtil.isEmptyString(this.activeRow.get("back_office_reason_code")));
        disabledMap.set("Payables are frozen for this payee", () => !(this.activeRow.get("payee_freeze_pay") == "Y"));
        disabledMap.set("Carrier invoice number required", () => !StringUtil.isEmptyString(this.activeRow.get("external_invoiceno")));
        disabledMap.set("Pay total must be greater than 0", () => triumphPayInfo.total.isGreaterThan(new Currency(0)));
        disabledMap.set("BOL received required", () => !triumphPayInfo.has_bol_received_block);
        disabledMap.set("BOL received date required", () => !triumphPayInfo.has_bol_received_date_block);
        disabledMap.set("Ready to bill required", () => !triumphPayInfo.has_ready_to_bill_block);
        disabledMap.set("Bill received date required", () => !triumphPayInfo.has_bill_received_date_block);
        disabledMap.set("There are unposted advances", () => !triumphPayInfo.has_unposted_wire_block);
        disabledMap.set("Triumph Audit invoice exceptions exist", () => !triumphPayInfo.has_critical_exception_block);

        return disabledMap;
    }

    private hasBeenSubmittedToTriumphPay(): boolean {
        return !StringUtil.isEmptyString(this.activeRow.get("triumphpay_pymt_id"));
    }

    setFieldsBasedOnPayMethod(payMethod: string) {
        const isHourlyOrSalary = [HOURLY, SALARY].includes(payMethod);
        this.textboxPayRate.enabled = !isHourlyOrSalary;
        this.textboxPerdiemPay.enabled = !isHourlyOrSalary;
        this.textboxPreloadCharge.enabled = !isHourlyOrSalary;

        if (isHourlyOrSalary) {
            this.activeRow.set("pay_rate", 0);
            this.activeRow.set("perdiem_pay", 0);
            this.activeRow.set("preload_charge", 0);
            this.activeRow.set("trailer_amt", 0);
            this.textboxPayUnits.text = "0";
        }
    }
}
